import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Bell, Check, Trash2, X, AlertTriangle, Info, CheckCircle2, Clock } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../lib/api';

const ICONS = {
  info: Info,
  reminder: Clock,
  warning: AlertTriangle,
  success: CheckCircle2,
};

const TYPE_COLOR = (theme) => ({
  info: theme.primary,
  reminder: theme.accent,
  warning: theme.warning,
  success: theme.success,
});

const formatRelative = (iso, t, dateLocale) => {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return t('notif_just_now');
    if (diff < 3600) return `${Math.floor(diff / 60)} ${t('notif_minutes_ago')}`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} ${t('notif_hours_ago')}`;
    return d.toLocaleDateString(dateLocale, { day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
};

const NotificationItem = ({ n, theme, color, Icon, onClick, onMarkRead, onRemove, t, dateLocale }) => (
  <li
    className="flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors group"
    style={{
      background: n.read ? 'transparent' : `${color}10`,
      borderBottom: `1px solid ${theme.border}`,
    }}
    onClick={() => onClick(n)}
    data-testid={`notification-item-${n.id}`}
  >
    <div className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center" style={{ background: `${color}20` }}>
      <Icon size={14} style={{ color }} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium truncate" style={{ color: theme.textPrimary }}>{n.title}</p>
      {n.body && (
        <p className="text-[11px] mt-0.5 line-clamp-2" style={{ color: theme.textSecondary }}>{n.body}</p>
      )}
      <p className="text-[10px] mt-1" style={{ color: theme.textMuted }}>{formatRelative(n.created_at, t, dateLocale)}</p>
    </div>
    <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
      {!n.read && (
        <button
          onClick={(e) => { e.stopPropagation(); onMarkRead(n.id); }}
          className="p-1"
          style={{ color: theme.textMuted }}
          title={t('notif_mark_one')}
        >
          <Check size={12} />
        </button>
      )}
      <button
        onClick={(e) => { e.stopPropagation(); onRemove(n.id); }}
        className="p-1"
        style={{ color: theme.textMuted }}
        title={t('notif_remove')}
      >
        <Trash2 size={12} />
      </button>
    </div>
  </li>
);

const EmptyState = ({ theme, t }) => (
  <div className="text-center py-10 px-4">
    <Bell size={28} className="mx-auto mb-2 opacity-30" style={{ color: theme.textMuted }} />
    <p className="text-sm" style={{ color: theme.textMuted }}>{t('notif_empty')}</p>
    <p className="text-[11px] mt-1" style={{ color: theme.textMuted }}>
      {t('notif_empty_hint')}
    </p>
  </div>
);

const NotificationCenter = ({ compact = false }) => {
  const { theme } = useTheme();
  const { t, dateLocale } = useLanguage();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(false);
  const [panelPos, setPanelPos] = useState(null);
  const bellRef = useRef(null);
  const panelRef = useRef(null);
  const seenIdsRef = useRef(new Set());

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/notifications');
      setItems(data.items || []);
      setUnread(data.unread || 0);

      // Fire browser native notifications for NEW unread items.
      if (typeof window !== 'undefined' && window.Notification && Notification.permission === 'granted') {
        (data.items || []).forEach((n) => {
          if (!n.read && !seenIdsRef.current.has(n.id)) {
            seenIdsRef.current.add(n.id);
            try {
              const nat = new Notification('Teko · ' + (n.title || ''), {
                body: n.body || '',
                tag: n.id,
                silent: false,
              });
              if (n.link) nat.onclick = () => {
                window.focus();
                navigate(n.link);
              };
            } catch (err) { console.debug('native notif failed', err); }
          } else if (n.read) {
            seenIdsRef.current.add(n.id);
          }
        });
      }
    } catch (err) {
      console.debug('notif fetch failed', err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Ask for permission once on first mount.
  useEffect(() => {
    if (typeof window !== 'undefined' && window.Notification && Notification.permission === 'default') {
      // Don't prompt aggressively — wait for the user to interact with the bell first.
    }
    fetchAll();
    const id = setInterval(fetchAll, 120_000); // poll every 2 minutes
    return () => clearInterval(id);
  }, [fetchAll]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      if (next && bellRef.current) {
        // Compute panel anchor in viewport coordinates
        const rect = bellRef.current.getBoundingClientRect();
        if (compact) {
          // mobile: panel under the bell, anchored to right
          setPanelPos({ top: rect.bottom + 8, right: Math.max(8, window.innerWidth - rect.right) });
        } else {
          // desktop sidebar: panel to the right of the bell
          setPanelPos({ top: rect.top, left: rect.right + 8 });
        }
      }
      return next;
    });
    if (typeof window !== 'undefined' && window.Notification && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  };

  const markRead = async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnread((u) => Math.max(0, u - 1));
    try { await api.put(`/notifications/${id}/read`); } catch (err) { console.debug(err); }
  };

  const remove = async (id) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
    try { await api.delete(`/notifications/${id}`); } catch (err) { console.debug(err); }
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
    try { await api.put('/notifications/read-all'); } catch (err) { console.debug(err); }
  };

  const handleClick = (n) => {
    if (!n.read) markRead(n.id);
    if (n.link) {
      navigate(n.link);
      setOpen(false);
    }
  };

  const colors = TYPE_COLOR(theme);

  return (
    <div className="relative" data-testid="notification-center">
      <button
        ref={bellRef}
        onClick={toggle}
        data-testid="notification-bell"
        aria-label={t('notif_aria')}
        className={`relative flex items-center ${compact ? 'p-2' : 'gap-3 w-full px-4 py-2.5'} rounded-md transition-all`}
        style={{ color: theme.textMuted }}
      >
        <Bell size={compact ? 20 : 18} strokeWidth={1.5} />
        {!compact && <span className="text-sm">{t('notifications')}</span>}
        {unread > 0 && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full text-[10px] flex items-center justify-center font-bold px-1"
            style={{ background: theme.error, color: '#fff' }}
            data-testid="notification-badge"
          >
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && panelPos && typeof document !== 'undefined' && createPortal(
        <div
          ref={panelRef}
          className="fixed z-[200] w-[min(380px,calc(100vw-1.5rem))] rounded-2xl overflow-hidden shadow-2xl"
          style={{
            background: theme.card,
            border: `1px solid ${theme.border}`,
            ...panelPos,
            maxHeight: 'calc(100vh - 2rem)',
          }}
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: `1px solid ${theme.border}` }}>
            <div className="flex items-center gap-2">
              <Bell size={14} style={{ color: theme.accent }} />
              <p className="text-sm font-medium" style={{ color: theme.textPrimary }}>{t('notifications')}</p>
              {unread > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: `${theme.accent}25`, color: theme.accent }}>
                  {unread} {t('notif_new_badge')}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  onClick={markAllRead}
                  className="text-[10px] px-2 py-1 rounded hover:opacity-80 transition"
                  style={{ color: theme.textMuted }}
                  data-testid="notification-mark-all-read"
                >
                  {t('notif_mark_all')}
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1" style={{ color: theme.textMuted }}>
                <X size={14} />
              </button>
            </div>
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {loading && items.length === 0 ? (
              <p className="text-center text-xs py-8" style={{ color: theme.textMuted }}>{t('loading')}</p>
            ) : items.length === 0 ? (
              <EmptyState theme={theme} t={t} />
            ) : (
              <ul>
                {items.map((n) => (
                  <NotificationItem
                    key={n.id}
                    n={n}
                    theme={theme}
                    color={colors[n.type] || theme.primary}
                    Icon={ICONS[n.type] || Info}
                    onClick={handleClick}
                    onMarkRead={markRead}
                    onRemove={remove}
                    t={t}
                    dateLocale={dateLocale}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default NotificationCenter;
